// pkg52 — "AI Modules" renamed to "Document Modules" across the nav, page titles, back-links, and helper text (D-53b/c); routes unchanged — delivered 2026-07-09
// pkg36 — AdminInternalModuleFillPage: staff fill an internal module in the admin portal (D-34i) — delivered 2026-07-03
//
// Reached from the "Fill Out" button on an internal module's card. The staff
// member optionally links the submission to a client file (the "this is about
// client X" link — the ethical wall hides blocked clients server-side), then
// steps through the shared ModuleWizard. Every fill creates a NEW submission.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import ModuleWizard from '../components/ModuleWizard';

export default function AdminInternalModuleFillPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [mod,     setMod]     = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const [linkedClientId, setLinkedClientId] = useState('');
  const [answers,    setAnswers]    = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [doneId,     setDoneId]     = useState(null);

  useEffect(() => {
    Promise.all([adminApi.getFirmModule(id), adminApi.listClients()])
      .then(([m, cs]) => {
        setMod(m);
        setClients(Array.isArray(cs) ? cs : []);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [id]);

  async function handleSubmit() {
    setSubmitting(true); setError('');
    try {
      const r = await adminApi.createInternalSubmission(id, answers, linkedClientId || undefined);
      setDoneId(r.submission_id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', padding: 30 }}>Loading…</div>;
  if (!mod)    return <div style={{ fontFamily: 'Arial, sans-serif', color: '#c00', padding: 30 }}>{error || 'Module not found.'}</div>;

  if (mod.audience !== 'internal') {
    return (
      <div style={{ fontFamily: 'Arial, sans-serif', color: '#555', padding: 30 }}>
        This module is filled by clients in their portal. Use Assign Clients on the Document Modules page instead.
      </div>
    );
  }

  if (doneId) {
    const linkedName = clients.find(c => c.id === linkedClientId)?.name;
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <h1 className="page-title">{mod.label}</h1>
        <div style={{ background: '#f0faf5', border: '1px solid #4caf7d', borderRadius: 6, padding: '20px 24px', maxWidth: 540, fontFamily: 'Arial, sans-serif' }}>
          <div style={{ fontSize: 22, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: '#1a3a2a' }}>Saved</div>
          <div style={{ fontSize: 14, color: '#444' }}>
            This submission is in View Submissions for this module{linkedName ? <> and is linked to <strong>{linkedName}</strong></> : ''}.
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button
              onClick={() => { setDoneId(null); setAnswers({}); setLinkedClientId(''); }}
              style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '7px 16px', borderRadius: 4, border: 'none', background: '#1a3a2a', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
            >
              Fill out another
            </button>
            <button
              onClick={() => navigate('/admin/dashboard/firm-modules')}
              style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '7px 16px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#1a3a2a', cursor: 'pointer', fontWeight: 600 }}
            >
              Back to Document Modules
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={{ marginBottom: 6 }}>
        <button
          onClick={() => navigate('/admin/dashboard/firm-modules')}
          style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, background: 'none', border: 'none', color: '#1a3a2a', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
        >
          ← Back to Document Modules
        </button>
      </div>

      <h1 className="page-title" style={{ marginBottom: 2 }}>{mod.label}</h1>
      {mod.description && (
        <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginTop: 0 }}>{mod.description}</p>
      )}

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '10px 14px', marginBottom: 16, fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#b91c1c' }}>
          {error}
        </div>
      )}

      {/* Optional client link */}
      <div style={{ background: '#f8fafb', border: '1px solid #dde3ec', borderRadius: 6, padding: '12px 16px', marginBottom: 18 }}>
        <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
          Link to a client file <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span>
        </label>
        <select
          value={linkedClientId}
          onChange={e => setLinkedClientId(e.target.value)}
          style={{ padding: '7px 10px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, minWidth: 280 }}
        >
          <option value="">— Not about a specific client —</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <ModuleWizard
        schema={mod.schema_json || { sections: [] }}
        answers={answers}
        onChange={(fieldId, val) => setAnswers(prev => ({ ...prev, [fieldId]: val }))}
        onSubmit={handleSubmit}
        submitting={submitting}
      />
    </div>
  );
}
